import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Autocomplete,
} from "@mui/material";
import { Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_BASE =
  (process.env.REACT_APP_API_URL || "").includes("undefined")
    ? "http://localhost:8000/api"
    : (process.env.REACT_APP_API_URL || "http://localhost:8000/api");

export default function VitalSignsRecord({ open, onClose, patient, encounter, ticketId = null }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    patient: patient?.id || "",
    ticket: ticketId || "",
    encounter: encounter?.id || "",
    temperature: "",
    blood_pressure_systolic: "",
    blood_pressure_diastolic: "",
    heart_rate: "",
    respiratory_rate: "",
    oxygen_saturation: "",
    blood_glucose: "",
    pain_score: "",
    weight: "",
    height: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setFormData({
        patient: patient?.id || "",
        ticket: ticketId || "",
        encounter: encounter?.id || "",
        temperature: "",
        blood_pressure_systolic: "",
        blood_pressure_diastolic: "",
        heart_rate: "",
        respiratory_rate: "",
        oxygen_saturation: "",
        blood_glucose: "",
        pain_score: "",
        weight: "",
        height: "",
        notes: "",
      });
      setErrors({});
    }
  }, [open, patient, encounter, ticketId]);

  const createVitalSigns = useMutation({
    mutationFn: (data) =>
      axios.post(`${API_BASE}/vital-signs/`, data, {
        headers: { Authorization: `Token ${token}` },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["vital-signs"]);
      if (encounter) {
        queryClient.invalidateQueries(["encounters", encounter.id]);
      }
      onClose();
    },
    onError: (error) => {
      console.error("Failed to record vital signs:", error);
      setErrors(error.response?.data || { non_field_errors: "Failed to record vital signs." });
    },
  });

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: "" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.patient) {
      newErrors.patient = t("nurse.errors.patientRequired", "Patient is required.");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Convert empty strings to null for optional fields; include ticket when from ticket view
    const submitData = { ...formData };
    Object.keys(submitData).forEach((key) => {
      if (submitData[key] === "" && key !== "notes") {
        submitData[key] = null;
      }
    });
    if (submitData.ticket === null || submitData.ticket === "") {
      delete submitData.ticket;
    }

    createVitalSigns.mutate(submitData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("nurse.recordVitalSigns", "Record Vital Signs")}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {createVitalSigns.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.non_field_errors || createVitalSigns.error?.message || t("nurse.errors.genericError", "An error occurred.")}
            </Alert>
          )}
          <Grid container spacing={2}>
            {!patient && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  {t("nurse.selectPatientFirst", "Please select a patient first")}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ step: "0.1", min: "0" }}
                label={t("nurse.temperature", "Temperature (°C)")}
                value={formData.temperature}
                onChange={handleChange("temperature")}
                error={!!errors.temperature}
                helperText={errors.temperature}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: "0" }}
                label={t("nurse.heartRate", "Heart Rate (bpm)")}
                value={formData.heart_rate}
                onChange={handleChange("heart_rate")}
                error={!!errors.heart_rate}
                helperText={errors.heart_rate}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: "0" }}
                label={t("nurse.bloodPressureSystolic", "BP Systolic (mmHg)")}
                value={formData.blood_pressure_systolic}
                onChange={handleChange("blood_pressure_systolic")}
                error={!!errors.blood_pressure_systolic}
                helperText={errors.blood_pressure_systolic}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: "0" }}
                label={t("nurse.bloodPressureDiastolic", "BP Diastolic (mmHg)")}
                value={formData.blood_pressure_diastolic}
                onChange={handleChange("blood_pressure_diastolic")}
                error={!!errors.blood_pressure_diastolic}
                helperText={errors.blood_pressure_diastolic}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: "0" }}
                label={t("nurse.respiratoryRate", "Respiratory Rate (bpm)")}
                value={formData.respiratory_rate}
                onChange={handleChange("respiratory_rate")}
                error={!!errors.respiratory_rate}
                helperText={errors.respiratory_rate}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: "0", max: "100" }}
                label={t("nurse.oxygenSaturation", "Oxygen Saturation (SpO2 %)")}
                value={formData.oxygen_saturation}
                onChange={handleChange("oxygen_saturation")}
                error={!!errors.oxygen_saturation}
                helperText={errors.oxygen_saturation}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ step: "0.1", min: "0" }}
                label={t("nurse.bloodGlucose", "Blood Glucose (mg/dL)")}
                value={formData.blood_glucose}
                onChange={handleChange("blood_glucose")}
                error={!!errors.blood_glucose}
                helperText={errors.blood_glucose}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: "0", max: "10" }}
                label={t("nurse.painScore", "Pain Score (0-10)")}
                value={formData.pain_score}
                onChange={handleChange("pain_score")}
                error={!!errors.pain_score}
                helperText={errors.pain_score}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ step: "0.1", min: "0" }}
                label={t("nurse.weight", "Weight (kg)")}
                value={formData.weight}
                onChange={handleChange("weight")}
                error={!!errors.weight}
                helperText={errors.weight}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ step: "0.1", min: "0" }}
                label={t("nurse.height", "Height (cm)")}
                value={formData.height}
                onChange={handleChange("height")}
                error={!!errors.height}
                helperText={errors.height}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t("nurse.notes", "Notes")}
                value={formData.notes}
                onChange={handleChange("notes")}
                error={!!errors.notes}
                helperText={errors.notes}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onClose}
            startIcon={<CancelIcon />}
            disabled={createVitalSigns.isPending}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={createVitalSigns.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={createVitalSigns.isPending || !patient}
          >
            {createVitalSigns.isPending ? t("common.saving", "Saving...") : t("common.save", "Save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
